/**
 * Native (Capacitor) invoice preview — writes a PDF to cache and dispatches an event
 * so portal shell can show HTML preview + Share the PDF (same pattern as export-xlsx).
 */

import { Capacitor } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'

export const MOBILE_INVOICE_PREVIEW_EVENT = 'spcc:mobile-invoice-preview-ready'

export interface MobileInvoicePreviewDetail {
  /** HTML used only for on-screen preview in the portal dialog */
  previewHtml: string
  fileName: string
  fileUri: string
  fileSizeBytes: number
}

function getCapacitorPlatform(): string {
  if (typeof Capacitor?.getPlatform === 'function') {
    return Capacitor.getPlatform()
  }
  return 'web'
}

export function isNativeCapacitorApp(): boolean {
  return typeof Capacitor?.isNativePlatform === 'function' ? Capacitor.isNativePlatform() : false
}

function normalizeFileUri(uri: string): string {
  if (uri.startsWith('file://') || uri.startsWith('content://') || uri.startsWith('http')) {
    return uri
  }
  if (uri.startsWith('/')) {
    return `file://${uri}`
  }
  return uri
}

function sanitizeFilename(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, ' ').trim()
}

export function ensurePdfInvoiceFilename(name: string): string {
  const trimmed = sanitizeFilename(name)
  if (!trimmed) return 'invoice.pdf'
  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`
}

async function tryWriteFile(base64: string, filename: string, directory: Directory): Promise<string> {
  const result = await Filesystem.writeFile({
    directory,
    path: filename,
    data: base64,
    recursive: true,
  })
  return normalizeFileUri(result.uri)
}

function getNativeWriteDirectories(platform: string): Directory[] {
  if (platform === 'android') {
    return [Directory.Cache, Directory.Data, Directory.ExternalCache, Directory.External]
  }
  return [Directory.Cache, Directory.Documents]
}

async function writePdfToNativeFilesystem(base64: string, filename: string): Promise<string> {
  const platform = getCapacitorPlatform()
  const directories = getNativeWriteDirectories(platform)

  for (const directory of directories) {
    try {
      return await tryWriteFile(base64, filename, directory)
    } catch {
      // try next directory
    }
  }

  throw new Error('Unable to write invoice PDF on native device')
}

function dispatchMobileInvoicePreview(detail: MobileInvoicePreviewDetail): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<MobileInvoicePreviewDetail>(MOBILE_INVOICE_PREVIEW_EVENT, { detail }))
}

/**
 * Writes invoice PDF bytes and emits {@link MOBILE_INVOICE_PREVIEW_EVENT}.
 * Portal shells mount invoice preview drawer (HTML preview + Share PDF file).
 */
export async function prepareNativeInvoicePreview(params: {
  previewHtml: string
  pdfBase64: string
  fileName: string
}): Promise<void> {
  const finalName = ensurePdfInvoiceFilename(params.fileName)
  const fileUri = await writePdfToNativeFilesystem(params.pdfBase64, finalName)
  const fileSizeBytes = Math.floor((params.pdfBase64.length * 3) / 4)

  dispatchMobileInvoicePreview({
    previewHtml: params.previewHtml,
    fileName: finalName,
    fileUri,
    fileSizeBytes,
  })
}
